import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { GetApartmentLookupItemResponseDtoListResponseDto } from '@/features/apartments/types/apartments-dto';
import { useAxiosAuth } from "@/hooks/useAxiosAuth";

const DEFAULT_PAGE_SIZE = 10;

type Props = {
    take?: number;
    skip?: number; 
    search?: string;
    hotelId?: string;
} & ApiHookProps;

export const useApartmentsLookupApi = ({ take = DEFAULT_PAGE_SIZE, skip, search, hotelId }: Props = {}) => {
    const axiosInstance = useAxiosAuth();

    const {
        data: apartmentsLookupList, 
        isLoading: isLoadingApartmentsLookupList, 
        isError: isErrorApartmentsLookupList,
        fetchNextPage: fetchApartmentsLookupListNextPage,
        hasNextPage: hasApartmentsLookupListNextPage,
        isFetchingNextPage: isFetchingApartmentsLookupListNextPage,
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
            ApiConfig.queries.features.apartments.queryKeys.apartmentsLookup, 
            take, skip, search, hotelId
        ],
        queryFn: async ({pageParam}): Promise<GetApartmentLookupItemResponseDtoListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.apartments.endpoints.apartmentsLookup, {    
                take, skip: pageParam, searchText: search?.trim(), hotelId
            });
            const response = await axiosInstance.get<GetApartmentLookupItemResponseDtoListResponseDto>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedApartmentsLookupList = apartmentsLookupList?.pages.flatMap((page) => page.items ?? []);

    return {
        apartmentsLookupList: fetchedApartmentsLookupList, 
        isLoadingApartmentsLookupList, 
        isErrorApartmentsLookupList,
        fetchApartmentsLookupListNextPage,
        hasApartmentsLookupListNextPage,
        isFetchingApartmentsLookupListNextPage,
    }
};
